/**
 * Multi-Year Forecast Page
 *
 * Investor-ready 4-year projections with:
 * - Historical data anchor from actual financials
 * - Adjustable growth rates for revenue & expenses
 * - Capital investment planning per year
 * - Tax projection per year using progressive brackets
 * - Side-by-side strategy comparison
 * - Professional chart visualizations
 * - PDF export for investor decks
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ComposedChart, Area, AreaChart,
} from 'recharts';
import {
  calculateTaxProjection,
  getEntityTaxBehavior,
  type FilingStatus,
  type EntityType,
} from '@bizsolutions/shared';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Save,
  RotateCcw,
  Trash2,
  TrendingUp,
  DollarSign,
  Landmark,
  Download,
  Target,
  Percent,
} from 'lucide-react';
import { exportForecastPDF } from '@/lib/pdf-export';

// ============================================
// Types
// ============================================

interface AnnualHistoryItem {
  year: number;
  yearStart: string;
  yearEnd: string;
  revenue: number;
  expenses: number;
  capitalExpenditures: number;
  deductibleExpenses: number;
  netIncome: number;
}

interface HistoryData {
  // 1-12. When != 12, year labels read as fiscal years (starting-year convention).
  fiscalYearEndMonth: number;
  annualData: AnnualHistoryItem[];
  topExpenseCategories: { category: string; amount: number }[];
  loanSummary: { annualPayments: number; totalBalance: number; activeLoanCount: number };
}

interface ForecastConfig {
  revenueGrowthRates: number[];  // % per year
  expenseGrowthRates: number[];  // % per year
  capitalInvestments: number[];  // $ per year
  capitalDeductionPct: number[]; // % deductible per year (Section 179 = 100%)
  additionalDeductions: number[]; // $ per year
  filingStatus: FilingStatus;
  entityType: EntityType;
  state: string;
  revenueTarget: number | null;  // $ target for final year
  notes: string;
}

interface YearProjection {
  year: number;
  revenue: number;
  expenses: number;
  capitalInvestment: number;
  totalDeductions: number;
  taxableIncome: number;
  federalTax: number;
  stateTax: number;
  selfEmploymentTax: number;
  totalTax: number;
  effectiveRate: number;
  netIncomeAfterTax: number;
  cumulativeRevenue: number;
  cumulativeNetIncome: number;
}

interface SavedForecast {
  id: string;
  name: string;
  description: string | null;
  baseYear: number;
  projectionYears: number;
  config: ForecastConfig | null;
  results: YearProjection[] | null;
  updatedAt: Date;
}

interface BusinessProfile {
  id: string;
  name: string;
}

// ============================================
// Constants
// ============================================

const currentYear = new Date().getFullYear();
const DEFAULT_YEARS = 4;

const FILING_STATUSES: { value: FilingStatus; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'head_of_household', label: 'Head of Household' },
];

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'single_member_llc', label: 'Single-Member LLC' },
  { value: 'partnership', label: 'Partnership' },
  { value: 's_corporation', label: 'S Corporation' },
  { value: 'c_corporation', label: 'C Corporation' },
];

function makeDefaultConfig(years: number): ForecastConfig {
  return {
    revenueGrowthRates: Array(years).fill(10),
    expenseGrowthRates: Array(years).fill(5),
    capitalInvestments: Array(years).fill(0),
    capitalDeductionPct: Array(years).fill(100),
    additionalDeductions: Array(years).fill(0),
    filingStatus: 'single',
    entityType: 'sole_proprietorship',
    state: '',
    revenueTarget: null,
    notes: '',
  };
}

function formatPct(rate: number) { return `${rate.toFixed(1)}%`; }
function formatCompact(value: number) {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return formatCurrency(value);
}

// ============================================
// Projection Engine (client-side, instant)
// ============================================

function computeProjections(
  baseRevenue: number,
  baseExpenses: number,
  baseYear: number,
  config: ForecastConfig,
  years: number,
): YearProjection[] {
  const projections: YearProjection[] = [];
  let prevRevenue = baseRevenue;
  let prevExpenses = baseExpenses;
  let cumulativeRevenue = 0;
  let cumulativeNetIncome = 0;

  for (let i = 0; i < years; i++) {
    const year = baseYear + i + 1;
    const revenueGrowth = (config.revenueGrowthRates[i] ?? 10) / 100;
    const expenseGrowth = (config.expenseGrowthRates[i] ?? 5) / 100;
    const capInvestment = config.capitalInvestments[i] ?? 0;
    const capDeductPct = (config.capitalDeductionPct[i] ?? 100) / 100;
    const additionalDeductions = config.additionalDeductions[i] ?? 0;

    const revenue = prevRevenue * (1 + revenueGrowth);
    const expenses = prevExpenses * (1 + expenseGrowth);
    const capitalDeduction = capInvestment * capDeductPct;
    const totalDeductions = expenses + capitalDeduction + additionalDeductions;

    const taxResult = calculateTaxProjection({
      grossRevenue: revenue,
      totalDeductions,
      filingStatus: config.filingStatus,
      entityType: config.entityType,
      state: config.state || null,
    });

    const netIncomeAfterTax = taxResult.netIncomeAfterTax;
    cumulativeRevenue += revenue;
    cumulativeNetIncome += netIncomeAfterTax;

    projections.push({
      year,
      revenue: Math.round(revenue),
      expenses: Math.round(expenses),
      capitalInvestment: capInvestment,
      totalDeductions: Math.round(totalDeductions),
      taxableIncome: Math.round(taxResult.taxableIncome),
      federalTax: Math.round(taxResult.federalTax),
      stateTax: Math.round(taxResult.stateTax),
      selfEmploymentTax: Math.round(taxResult.selfEmploymentTax),
      totalTax: Math.round(taxResult.totalTax),
      effectiveRate: taxResult.totalEffectiveRate,
      netIncomeAfterTax: Math.round(netIncomeAfterTax),
      cumulativeRevenue: Math.round(cumulativeRevenue),
      cumulativeNetIncome: Math.round(cumulativeNetIncome),
    });

    prevRevenue = revenue;
    prevExpenses = expenses;
  }

  return projections;
}

// ============================================
// Component
// ============================================

export function ForecastPage() {
  const queryClient = useQueryClient();

  const [businessProfileId, setBusinessProfileId] = useState<string>('all');
  const [projectionYears, setProjectionYears] = useState(DEFAULT_YEARS);
  const [config, setConfig] = useState<ForecastConfig>(makeDefaultConfig(DEFAULT_YEARS));
  const [forecastName, setForecastName] = useState('');
  const [activeForecastId, setActiveForecastId] = useState<string | null>(null);

  // Load business profiles
  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const selectedProfileName = profiles.find(p => p.id === businessProfileId)?.name;

  // Load historical data
  const { data: historyData } = useQuery({
    queryKey: ['forecast-history', businessProfileId],
    queryFn: async () => {
      const result = await api.getForecastHistory({
        yearsBack: 3,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as HistoryData;
    },
  });

  // Load saved forecasts
  const { data: savedForecasts } = useQuery({
    queryKey: ['forecasts', businessProfileId],
    queryFn: async () => {
      const result = await api.listForecasts({
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return (result.data || []) as SavedForecast[];
    },
  });

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (activeForecastId) return api.updateForecast(activeForecastId, data);
      return api.createForecast(data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forecasts'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteForecast(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forecasts'] });
      if (activeForecastId) resetForecast();
    },
  });

  // Derive base year data
  const history = historyData?.annualData || [];
  const latestYear = history.length > 0 ? history[history.length - 1] : null;
  const baseYear = latestYear?.year || currentYear;
  const baseRevenue = latestYear?.revenue || 0;
  const baseExpenses = latestYear?.expenses || 0;
  const fiscalYearEndMonth = historyData?.fiscalYearEndMonth ?? 12;
  const isFiscalYear = fiscalYearEndMonth !== 12;
  const yearLabelPrefix = isFiscalYear ? 'FY ' : '';
  const fiscalYearEndMonthName = isFiscalYear
    ? new Date(2000, fiscalYearEndMonth - 1, 1).toLocaleString('en-US', { month: 'long' })
    : '';

  // Live projections
  const projections = useMemo(() => {
    return computeProjections(baseRevenue, baseExpenses, baseYear, config, projectionYears);
  }, [baseRevenue, baseExpenses, baseYear, config, projectionYears]);

  // Chart data combining history + projections
  const chartData = useMemo(() => {
    const historyItems = history.map(h => ({
      year: `${yearLabelPrefix}${h.year}`,
      revenue: h.revenue,
      expenses: h.expenses,
      netIncome: h.netIncome,
      type: 'actual' as const,
    }));
    const projItems = projections.map(p => ({
      year: `${yearLabelPrefix}${p.year}`,
      revenue: p.revenue,
      expenses: p.expenses,
      netIncome: p.netIncomeAfterTax,
      type: 'projected' as const,
    }));
    return [...historyItems, ...projItems];
  }, [history, projections]);

  // Tax rate chart data
  const taxChartData = useMemo(() => {
    return projections.map(p => ({
      year: p.year.toString(),
      federalTax: p.federalTax,
      stateTax: p.stateTax,
      seTax: p.selfEmploymentTax,
      effectiveRate: +(p.effectiveRate * 100).toFixed(1),
    }));
  }, [projections]);

  // Summary stats
  const finalYear = projections[projections.length - 1];
  const totalProjectedRevenue = projections.reduce((s, p) => s + p.revenue, 0);
  const totalProjectedTax = projections.reduce((s, p) => s + p.totalTax, 0);
  const revenueCAGR = projections.length > 0 && baseRevenue > 0
    ? (Math.pow(finalYear.revenue / baseRevenue, 1 / projectionYears) - 1) * 100
    : 0;

  // Config updaters
  const updateConfigArray = useCallback((field: keyof ForecastConfig, index: number, value: number) => {
    setConfig(prev => {
      const arr = [...(prev[field] as number[])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  }, []);

  const updateConfigField = useCallback(<K extends keyof ForecastConfig>(field: K, value: ForecastConfig[K]) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForecast = useCallback(() => {
    setConfig(makeDefaultConfig(projectionYears));
    setForecastName('');
    setActiveForecastId(null);
  }, [projectionYears]);

  const loadForecast = useCallback((f: SavedForecast) => {
    setActiveForecastId(f.id);
    setForecastName(f.name);
    if (f.config) {
      setConfig(f.config);
      setProjectionYears(f.projectionYears);
    }
  }, []);

  const saveForecast = useCallback(() => {
    saveMutation.mutate({
      businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      name: forecastName || `Forecast ${new Date().toLocaleDateString()}`,
      baseYear,
      projectionYears,
      config,
      results: projections,
    });
  }, [saveMutation, businessProfileId, forecastName, baseYear, projectionYears, config, projections]);

  // Ensure config arrays match projection years
  const ensureArrayLength = useCallback((years: number) => {
    setConfig(prev => ({
      ...prev,
      revenueGrowthRates: Array.from({ length: years }, (_, i) => prev.revenueGrowthRates[i] ?? 10),
      expenseGrowthRates: Array.from({ length: years }, (_, i) => prev.expenseGrowthRates[i] ?? 5),
      capitalInvestments: Array.from({ length: years }, (_, i) => prev.capitalInvestments[i] ?? 0),
      capitalDeductionPct: Array.from({ length: years }, (_, i) => prev.capitalDeductionPct[i] ?? 100),
      additionalDeductions: Array.from({ length: years }, (_, i) => prev.additionalDeductions[i] ?? 0),
    }));
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Multi-Year Forecast</h1>
          <p className="text-muted-foreground">
            {baseRevenue > 0
              ? `Based on ${yearLabelPrefix}${baseYear} actuals: ${formatCurrency(baseRevenue)} revenue, ${formatCurrency(baseExpenses)} expenses`
              : 'Enter base values or add financial data to anchor projections'}
          </p>
          {isFiscalYear && (
            <p className="text-xs text-muted-foreground italic mt-1">
              Aligned to fiscal year ending {fiscalYearEndMonthName} 31. Years are labeled FY (start year).
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={projectionYears.toString()}
            onValueChange={(v) => {
              const yrs = parseInt(v);
              setProjectionYears(yrs);
              ensureArrayLength(yrs);
            }}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2, 3, 4, 5, 6].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}-Year Forecast</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => exportForecastPDF(
            history, projections, config, baseYear, selectedProfileName,
          )}>
            <Download className="h-4 w-4 mr-1" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Year {finalYear?.year} Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{formatCompact(finalYear?.revenue || 0)}</div>
            <p className="text-xs text-muted-foreground">CAGR: {formatPct(revenueCAGR)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Year {finalYear?.year} Tax</CardTitle>
            <Landmark className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-destructive">{formatCompact(finalYear?.totalTax || 0)}</div>
            <p className="text-xs text-muted-foreground">Eff. rate: {formatPct((finalYear?.effectiveRate || 0) * 100)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Year {finalYear?.year} Net Income</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-emerald-600">{formatCompact(finalYear?.netIncomeAfterTax || 0)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">{projectionYears}yr Total Revenue</CardTitle>
            <Target className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-blue-600">{formatCompact(totalProjectedRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">{projectionYears}yr Total Tax</CardTitle>
            <Percent className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-amber-600">{formatCompact(totalProjectedTax)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Growth Settings + Save/Load */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tax Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tax Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Entity Type</Label>
              <Select value={config.entityType} onValueChange={(v) => updateConfigField('entityType', v as EntityType)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {getEntityTaxBehavior(config.entityType).form}
              </p>
            </div>
            {getEntityTaxBehavior(config.entityType).taxMethod === 'pass_through' ? (
              <div>
                <Label className="text-xs">Filing Status</Label>
                <Select value={config.filingStatus} onValueChange={(v) => updateConfigField('filingStatus', v as FilingStatus)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FILING_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="rounded-md bg-muted/50 p-2">
                <p className="text-xs text-muted-foreground">C-Corp: flat 21% federal (Form 1120)</p>
              </div>
            )}
            <div>
              <Label className="text-xs">State</Label>
              <Input
                className="mt-1"
                value={config.state}
                onChange={e => updateConfigField('state', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="TX, CA, NY"
                maxLength={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Year-by-Year Growth Rates */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Year-by-Year Assumptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="text-left py-2 pr-2">Year</th>
                    <th className="text-right py-2 px-1">Rev Growth %</th>
                    <th className="text-right py-2 px-1">Exp Growth %</th>
                    <th className="text-right py-2 px-1">Capital ($)</th>
                    <th className="text-right py-2 px-1">Cap Deduct %</th>
                    <th className="text-right py-2 pl-1">Other Deduct ($)</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: projectionYears }, (_, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 pr-2 font-medium">{baseYear + i + 1}</td>
                      <td className="py-1.5 px-1">
                        <Input
                          type="number"
                          className="h-7 w-16 text-right text-xs ml-auto"
                          value={config.revenueGrowthRates[i] ?? 10}
                          onChange={e => updateConfigArray('revenueGrowthRates', i, parseFloat(e.target.value) || 0)}
                          step={1}
                        />
                      </td>
                      <td className="py-1.5 px-1">
                        <Input
                          type="number"
                          className="h-7 w-16 text-right text-xs ml-auto"
                          value={config.expenseGrowthRates[i] ?? 5}
                          onChange={e => updateConfigArray('expenseGrowthRates', i, parseFloat(e.target.value) || 0)}
                          step={1}
                        />
                      </td>
                      <td className="py-1.5 px-1">
                        <Input
                          type="number"
                          className="h-7 w-20 text-right text-xs ml-auto"
                          value={config.capitalInvestments[i] ?? 0}
                          onChange={e => updateConfigArray('capitalInvestments', i, parseFloat(e.target.value) || 0)}
                          step={1000}
                          min={0}
                        />
                      </td>
                      <td className="py-1.5 px-1">
                        <Input
                          type="number"
                          className="h-7 w-16 text-right text-xs ml-auto"
                          value={config.capitalDeductionPct[i] ?? 100}
                          onChange={e => updateConfigArray('capitalDeductionPct', i, parseFloat(e.target.value) || 0)}
                          min={0}
                          max={100}
                          step={5}
                        />
                      </td>
                      <td className="py-1.5 pl-1">
                        <Input
                          type="number"
                          className="h-7 w-20 text-right text-xs ml-auto"
                          value={config.additionalDeductions[i] ?? 0}
                          onChange={e => updateConfigArray('additionalDeductions', i, parseFloat(e.target.value) || 0)}
                          step={1000}
                          min={0}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Save/Load */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Forecasts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Forecast Name</Label>
              <Input
                className="mt-1"
                value={forecastName}
                onChange={e => setForecastName(e.target.value)}
                placeholder="e.g. Conservative Growth"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveForecast} disabled={saveMutation.isPending} className="flex-1">
                <Save className="h-3 w-3 mr-1" />
                {activeForecastId ? 'Update' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={resetForecast}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>
            {(savedForecasts || []).length > 0 && (
              <div className="border-t pt-3 space-y-1 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Saved Forecasts</p>
                {(savedForecasts || []).map(f => (
                  <div key={f.id} className="flex items-center justify-between gap-1">
                    <Button
                      variant={activeForecastId === f.id ? 'default' : 'ghost'}
                      size="sm"
                      className="flex-1 justify-start text-xs h-7 truncate"
                      onClick={() => loadForecast(f)}
                    >
                      {f.name}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(f.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue & Income Projection */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue & Net Income Projection</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="year" className="text-xs" />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} opacity={0.8} />
                <Bar dataKey="expenses" name="Expenses" fill="#f97316" radius={[4, 4, 0, 0]} opacity={0.6} />
                <Line type="monotone" dataKey="netIncome" name="Net Income" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tax Projection */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Liability & Effective Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={taxChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="year" className="text-xs" />
                <YAxis yAxisId="left" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} className="text-xs" />
                <Tooltip formatter={(value, name) => {
                  if (name === 'Effective Rate') return `${value}%`;
                  return formatCurrency(Number(value));
                }} />
                <Legend />
                <Bar yAxisId="left" dataKey="federalTax" name="Federal Tax" stackId="tax" fill="#ef4444" />
                <Bar yAxisId="left" dataKey="seTax" name="SE Tax" stackId="tax" fill="#f97316" />
                {taxChartData.some(d => d.stateTax > 0) && (
                  <Bar yAxisId="left" dataKey="stateTax" name="State Tax" stackId="tax" fill="#eab308" />
                )}
                <Line yAxisId="right" type="monotone" dataKey="effectiveRate" name="Effective Rate" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Cumulative Growth Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Cumulative Growth Trajectory</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={projections.map(p => ({
              year: p.year.toString(),
              cumulativeRevenue: p.cumulativeRevenue,
              cumulativeNetIncome: p.cumulativeNetIncome,
            }))}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="year" className="text-xs" />
              <YAxis tickFormatter={(v) => formatCompact(v)} className="text-xs" />
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              <Legend />
              <Area type="monotone" dataKey="cumulativeRevenue" name="Cumulative Revenue" fill="#3b82f6" fillOpacity={0.15} stroke="#3b82f6" strokeWidth={2} />
              <Area type="monotone" dataKey="cumulativeNetIncome" name="Cumulative Net Income" fill="#10b981" fillOpacity={0.15} stroke="#10b981" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Projection Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Year-by-Year Projections</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 pr-4">Metric</th>
                  {history.length > 0 && (
                    <th className="text-right py-2 px-3 bg-muted/30">{baseYear} (Actual)</th>
                  )}
                  {projections.map(p => (
                    <th key={p.year} className="text-right py-2 px-3">{p.year}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Revenue</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono bg-muted/30">{formatCompact(latestYear.revenue)}</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono">{formatCompact(p.revenue)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Operating Expenses</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono bg-muted/30">{formatCompact(baseExpenses)}</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono">{formatCompact(p.expenses)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Capital Investment</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono bg-muted/30">{formatCompact(latestYear.capitalExpenditures)}</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono">{formatCompact(p.capitalInvestment)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Total Deductions</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono text-emerald-600 bg-muted/30">{formatCompact(latestYear.deductibleExpenses)}</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono text-emerald-600">{formatCompact(p.totalDeductions)}</td>
                  ))}
                </tr>
                <tr className="border-b bg-muted/20">
                  <td className="py-2 pr-4 font-semibold">Total Tax</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono font-semibold text-destructive bg-muted/30">—</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono font-semibold text-destructive">{formatCompact(p.totalTax)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Effective Tax Rate</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono bg-muted/30">—</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono">{formatPct(p.effectiveRate * 100)}</td>
                  ))}
                </tr>
                <tr className="border-t-2 border-foreground">
                  <td className="py-2 pr-4 font-bold text-base">Net Income (After Tax)</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono font-bold text-base bg-muted/30">{formatCompact(latestYear.netIncome)}</td>}
                  {projections.map(p => (
                    <td key={p.year} className={`py-2 px-3 text-right font-mono font-bold text-base ${p.netIncomeAfterTax >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                      {formatCompact(p.netIncomeAfterTax)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-2 pr-4 font-medium">Quarterly Tax Reserve</td>
                  {latestYear && <td className="py-2 px-3 text-right font-mono bg-muted/30">—</td>}
                  {projections.map(p => (
                    <td key={p.year} className="py-2 px-3 text-right font-mono">{formatCompact(p.totalTax / 4)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
